import { DialogM } from "@common/components/alert/dialog/Dialog"
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import FilledButtonM from "@common/components/button/FilledButtonM"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { useApiStatus } from "@common/hooks/useApiStatus"
import useLaunchActivity from "@common/hooks/useLaunchActivity"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import { hasRole } from "@data/utility/ability"
import {
    countSameActivitiesToday,
    countSameAgainActivities,
    findItemInListWithId,
} from "@data/utility/dataUtils"
import { whatIsIt } from "@data/utility/Utils"
import { StatusBar } from "@common/systemBars/StatusBar"
import React, { useContext, useEffect, useState } from "react"
import { RefreshControl, ScrollView, View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import ActivityTemplateHeader from "./ActivityTemplateHeader"
import SingleActivityResult from "./SingleActivityResult"

const ActivityResultScreen = (props) => {
    const { navigation, route } = props
    const { studentId, activityIds, noLaunchButton, fromNotification } =
        route.params

    const liveSession = props.currentLiveSession
    const studentsList = props.isLiveMode
        ? liveSession?.users
        : props.user?.students

    const user = studentId
        ? studentsList?.find((s) => s.id === parseInt(studentId))
        : props.user

    const userChallenges = user?.challenges?.concat(user?.history?.challenges)

    const [resultActivities, setResultActivities] = useState([])
    const activityIdsString =
        whatIsIt(activityIds) === "Array" ? activityIds?.join(",") : activityIds

    const activities = resultActivities
    const activity = activities?.[0]
    const activityTemplate = activity
        ? findItemInListWithId(
              activity?.activity_template_id,
              props.activityTemplates
          )
        : null
    const challenge = activity?.challenge_id
        ? findItemInListWithId(activity?.challenge_id, userChallenges)
        : null
    const schoolSubject = activity?.school_subject_id
        ? findItemInListWithId(
              activity?.school_subject_id,
              props.schoolSubjects
          )
        : challenge?.school_subject

    const sameActivitiesCount = challenge?.planning_mode
        ? countSameAgainActivities(activityTemplate, activities, activity?.id)
        : countSameActivitiesToday(
              activityTemplate,
              activities,
              challenge ? challenge?.id : null,
              schoolSubject ? schoolSubject?.id : null
          )

    const maxReached = sameActivitiesCount >= activityTemplate?.max_per_day

    const maxReachedTitle = "Nombre max atteint"
    const maxReachedText =
        "Tu as atteint le maximum d'activités '" +
        activityTemplate?.title +
        "' sur " +
        (challenge?.id ? "ce défi" : "cette matière") +
        " aujourd'hui. Tu peux faire d'autres activités ou revenir demain."
    const requestApiService = hasRole(props.user, "student")
        ? activitiesService.activityTodoAsStudent
        : activitiesService.activityTodoAsTutor
    const launchActivity = useLaunchActivity()

    const { sendLiveKitData: sendNewTodo } = useLiveKitEventSender()

    useApiStatus(requestApiService, null, true, (data) => {
        if (hasRole(props.user, "student")) {
            props.apiRequest(
                activitiesService.lockActivityV2,
                { id: data?.id },
                {
                    lock: true,
                    live_mode: props.isLiveMode,
                    live_session_id: props.liveSessionId ?? undefined,
                }
            )
        } else {
            SnackBarM.show({
                text1: "✅ La même activité a été ajoutée à la liste de l'élève",
                duration: 4000,
            })
            navigation.goBack()
        }

        sendNewTodo(
            "newTodo",
            {
                id: studentId ?? props.user?.id,
                activity: data,
            },
            [studentId ?? props.user?.id]
        )
    })

    useApiStatus(activitiesService.lockActivityV2, null, true, (data) => {
        launchActivity(
            {
                activity_template_id: activityTemplate?.id,
                id: data.id,
                school_subject_id: schoolSubject?.id,
                chapterName: activity?.student_chapter?.chapter?.name,
                chapterId: activity?.student_chapter?.id,
            },
            "replace"
        )
    })

    const onPress = () => {
        if (maxReached) {
            DialogM.show({
                text1: maxReachedTitle,
                text2: maxReachedText,
            })
            return
        }
        props.apiRequest(
            requestApiService,
            hasRole(props.user, "student") ? {} : { id: studentId },
            {
                activity_template_id: activityTemplate?.id,
                school_subject_id: schoolSubject?.id,
                material_id: activity?.material?.id,
                material_chapter: activity?.chapter,
                estimated_duration: activity?.estimated_duration,
                chapter_id: activity?.student_chapter?.chapter?.id,
                chapter_name: activity?.student_chapter?.chapter?.name,
                chapter_class: activity?.student_chapter?.chapter?.class,
            }
        )
    }

    const refresh = () => {
        props.apiRequest(
            activitiesService.getActivities,
            {},
            {},
            {
                with_logs: 1,
                activity_ids: activityIdsString,
            }
        )
    }

    useEffect(() => {
        if (!activity?.activity_logs || fromNotification) refresh()
    }, [])

    useApiStatus(activitiesService.getActivities, null, true, (data) => {
        setResultActivities(data)
    })

    const dimensions = useContext(DimensionsContext)

    return (
        <View
            style={{
                flex: 1,
                alignItems: "center",
                justifyContent: "flex-start",
            }}
        >
            <ActivityTemplateHeader
                activityTemplate={activityTemplate}
                activity={activity}
                challenge={challenge}
                navigation={navigation}
                previous
                schoolSubject={schoolSubject}
            />

            {IS_WEB && !!props.getActivitiesRequest ? (
                <ActivityIndicator
                    animating={true}
                    style={{ marginTop: 10 }}
                    color={theme.colors.primary}
                />
            ) : (
                <ScrollView
                    style={{
                        flex: 1,
                        backgroundColor: theme.colors.whiteAlmost,
                    }}
                    refreshControl={
                        <RefreshControl
                            refreshing={!!props.getActivitiesRequest}
                            onRefresh={refresh}
                        />
                    }
                >
                    {activities?.map((a, index) => (
                        <SingleActivityResult
                            key={index}
                            activity={a}
                            activityIdsString={activityIdsString}
                        />
                    ))}
                    <View style={{ height: 120 }} />
                </ScrollView>
            )}

            {!challenge?.finished_at &&
                !challenge?.abandoned_at &&
                !noLaunchButton &&
                !maxReached &&
                !!activity?.finished_at && (
                    <View
                        style={{
                            backgroundColor: theme.colors.primaryLighter,
                            paddingBottom: 10,
                            paddingTop: 20,
                            borderTopWidth: 1,
                            borderColor: theme.colors.lightMore,
                            position: "absolute",
                            width: "100%",
                            alignItems: "center",
                            bottom: 0,
                        }}
                    >
                        <FilledButtonM
                            color={theme.colors.grey}
                            onPress={onPress}
                            style={{
                                width: dimensions?.width - 20,
                            }}
                            icon={"redo"}
                            loading={
                                props.activityTodoAsStudentRequest ||
                                props.activityTodoAsTutorRequest ||
                                props.lockActivityV2Request
                            }
                            label={
                                hasRole(props.user, "student")
                                    ? "Refaire l'activité"
                                    : "Redonner l'activité"
                            }
                        />
                    </View>
                )}

            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}
const mapStateToProps = (state) => {
    return {
        getActivitiesRequest: state.api.getActivities?.request,
        getActivitiesSuccess: state.api.getActivities?.success,
        getActivitiesError: state.api.getActivities?.error,
        activityTemplates: state.data.staticData?.activity_templates,
        schoolSubjects: state.data.staticData?.school_subjects,
        user: state.data.currentUser?.object,
        liveSessions: state.data.currentUser?.object?.live_sessions,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        liveSessionId: state.liveMode?.liveSessionId,
        isLiveMode: state.liveMode.isLiveModeEnabled,
        activityTodoAsStudentRequest: state.api.activityTodoAsStudent?.request,
        activityTodoAsTutorRequest: state.api.activityTodoAsTutor?.request,
        lockActivityV2Request: state.api.lockActivityV2?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ActivityResultScreen)
